import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../AuthContext';
import { useProfileSheet } from '../../../../contexts/ProfileSheetContext';
import { ContentType, SubType } from '../../../../api/types';
import NoBrandBlock from '../blocks/NoBrandBlock';
import StatsBlock from '../blocks/StatsBlock';
import FilterBlock from '../blocks/FilterBlock';
import RssStatusBlock from '../blocks/RssStatusBlock';
import ExtractionQueueBlock from '../blocks/ExtractionQueueBlock';
import PublishSchedulerBlock from '../blocks/PublishSchedulerBlock';
import EmulationBlock from '../blocks/EmulationBlock';
import ChangePasswordInline from '../blocks/ChangePasswordInline';
import IdentityFooter from '../blocks/IdentityFooter';
import SectionTitle from '../shared/SectionTitle';
import { useAdminQueues } from '../useAdminQueues';

interface FilterState {
  activeContentTypes: Set<ContentType>;
  activeSubTypes: Set<SubType>;
}

interface ProfileViewProps {
  filterState: FilterState;
  updateContentTypes: (types: Set<ContentType>) => void;
  updateSubTypes: (types: Set<SubType>) => void;
}

/** The signed-in profile: stats, preferences, admin blocks, identity footer. */
const ProfileView: React.FC<ProfileViewProps> = ({
  filterState,
  updateContentTypes,
  updateSubTypes,
}) => {
  const { user, logout } = useAuth();
  const { isOpen, view, closeSheet, setView } = useProfileSheet();
  const navigate = useNavigate();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const active = isOpen && view === 'profile';
  // Reserved surface: the whole Superadmin Controls section (queues,
  // scheduler, emulation) is true-superadmin only (CS excluded)
  const isSuperAdmin = !!user?.is_true_super_admin;
  const hasVerifiedBrand = !!user?.brands?.find((b) => b.verified && b.slug);
  const {
    extractionQueue, rssStatus, publisherStatus,
    toggleExtractionPause, toggleRssPause, togglePublisherPause,
  } = useAdminQueues(active && isSuperAdmin);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      closeSheet();
      navigate('/');
    }
  };

  if (!user) return null;

  return (
    <>
      {/* Scrolling middle — everything except identity. While the
          change-password flow is open, a local frost veil softens this
          region so focus stays on the pinned form; clicking it dismisses. */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <div className="tend-sheet-body tend-scroll" style={{ paddingTop: 4 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {!hasVerifiedBrand && <NoBrandBlock />}

          <SectionTitle meta="Last 30 days">Activity</SectionTitle>
          <StatsBlock active={active} />

          <SectionTitle>Content Filters</SectionTitle>
          <FilterBlock
            filterState={filterState}
            updateContentTypes={updateContentTypes}
            updateSubTypes={updateSubTypes}
          />

          <SectionTitle>Connect</SectionTitle>
          <button
            type="button"
            className="tend-row"
            onClick={() => setView('api-keys')}
            aria-label="API keys"
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div>API keys</div>
              <div className="tend-row-hint">Connect Claude Desktop, claude.ai or Claude Code to TEND</div>
            </div>
            <svg
              className="tend-row-chevron"
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path strokeLinecap="round" d="M9 6l6 6-6 6" />
            </svg>
          </button>

          {isSuperAdmin && (
            <>
              <SectionTitle>Superadmin Controls</SectionTitle>
              {rssStatus && (
                <RssStatusBlock rssStatus={rssStatus} onToggle={toggleRssPause} />
              )}
              {extractionQueue && (
                <ExtractionQueueBlock
                  extractionQueue={extractionQueue}
                  onToggle={toggleExtractionPause}
                />
              )}
              {publisherStatus && (
                <PublishSchedulerBlock
                  publisherStatus={publisherStatus}
                  onToggle={togglePublisherPause}
                />
              )}
              <EmulationBlock active={active} />
            </>
          )}
        </div>
      </div>
      {changePasswordOpen && (
        <div
          className="tend-body-veil"
          onClick={() => setChangePasswordOpen(false)}
          aria-hidden="true"
        />
      )}
      </div>

      {/* Pinned foot — the anchor: change-password expander + identity/controls */}
      <div className="tend-sheet-foot">
        <ChangePasswordInline
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
        <IdentityFooter
          changePasswordOpen={changePasswordOpen}
          onToggleChangePassword={() => setChangePasswordOpen((v) => !v)}
          onLogout={handleLogout}
        />
      </div>
    </>
  );
};

export default ProfileView;
